import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { BASE_URL } from '@/lib/api-client';

export const Role = {
  ADMIN: 'ADMIN',
  USER: 'USER',
} as const;

export type Role = typeof Role[keyof typeof Role];

export interface User {
  id: string;
  username: string;
  password?: string;
  role: Role;
  permissions: string[];
  createdAt: number;
  expiresAt: number | null;
  isActive: boolean;
}

export const PERMISSIONS = [
  { id: 'banking-app', label: 'Banking App Demo' },
  { id: 'bbps-mobile', label: 'BBPS App Demo' },
  { id: 'merchant-onboarding', label: 'Merchant Onboarding Demo' },
  { id: 'cbs-mobile', label: 'CBS Mobile Application Demo' },
  { id: 'bank-demo', label: 'Bank Onboarding Demo' },
  { id: 'cbs-demo', label: 'CBS Onboarding Demo' },
  { id: 'los-lms', label: 'LOS, LMS & Digital Lending Demo' },
];

export const MAX_VALIDITY_DAYS = 15;

interface AuthState {
  currentUser: User | null;
  token: string | null;
  authorizedModules: string[];
  login: (username: string, password?: string) => Promise<User | null>;
  logout: () => void;
  setAuthorizedModules: (modules: string[]) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      currentUser: null,
      token: null,
      authorizedModules: [],

      setAuthorizedModules: (modules) => set({ authorizedModules: modules }),

      login: async (username, password) => {
        try {
          const response = await axios.post(`${BASE_URL}/auth/login`, {
            username,
            password
          }, {
            headers: {
              'accept': '*/*',
              'Content-Type': 'application/json'
            }
          });

          if (response.data.success) {
            const { token, role, username: apiUsername } = response.data.data;

            const user: User = {
              id: apiUsername,
              username: apiUsername,
              role: role.toUpperCase() === 'ADMIN' ? Role.ADMIN : Role.USER,
              permissions: role.toUpperCase() === 'ADMIN' ? PERMISSIONS.map(p => p.id) : [],
              createdAt: Date.now(),
              expiresAt: null,
              isActive: true,
            };

            set({ currentUser: user, token: token, authorizedModules: [] });
            return user;
          } else {
            throw new Error(response.data.message || 'Authentication failed.');
          }
        } catch (error: any) {
          const message = error.response?.data?.message || error.message || 'Secure gateway communication failure.';
          throw new Error(message);
        }
      },

      logout: () => {
        set({ currentUser: null, token: null, authorizedModules: [] });
        localStorage.removeItem('auth-storage');
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ currentUser: state.currentUser, token: state.token }),
    }
  )
);
